/**
 * The registry of TSCN node types. Each type self-registers a parser, which turns snake_case
 * body properties into its typed shape (`material_override` → `materialOverride`), and an
 * optional formatter for `<NodeDetailsPanel>`. Rendering lives in `r3f/NodeComponentRegistry.ts`.
 */
import { isPropertyOverrideHeading } from '../parser/utils';
import { headingIndexField } from '../parser/rawNode';
import { INSTANCE_PLACEHOLDER_TYPE } from '../godot/scene/packedScene';
import { warn } from '../logger';
import { createTypeRegistry } from './createTypeRegistry';
class NodeRegistry {
    // The shared ADR-0002 registry, so the parser, 3D-render and 2D-render domains share one
    // overwrite-with-a-warn contract.
    registrations = createTypeRegistry('NodeRegistry');
    register(registration) {
        this.registrations.register(registration.typeName, registration);
    }
    /** Remove one registration (test teardown for probe types); true if it existed. */
    unregister(typeName) {
        return this.registrations.unregister(typeName);
    }
    findRegistration(heading) {
        // Only a `[node]` heading resolves: a `[sub_resource type="BoxMesh"]` must never match a
        // node typeName.
        if (heading.type !== 'node')
            return null;
        const type = heading.attributes.type;
        if (!type)
            return null;
        return this.registrations.get(type) ?? null;
    }
    getRegistration(typeName) {
        return this.registrations.get(typeName) ?? null;
    }
    getAllTypeNames() {
        return this.registrations.getAllTypeNames();
    }
    clear() {
        this.registrations.clear();
    }
}
export const nodeRegistry = new NodeRegistry();
export function parseNodeWithRegistry(heading, properties) {
    const instanceRef = heading.attributes.instance || properties.instance;
    // An `instance_placeholder=` heading is an InstancePlaceholder node
    // (packed_scene.cpp:255): a node of its own, not an override.
    const placeholderPath = heading.attributes.instance_placeholder;
    const hasInstanceAttribute = !!instanceRef || !!placeholderPath;
    const registration = nodeRegistry.findRegistration(heading);
    // The base Node fallback keeps unsupported types and instance nodes in the tree.
    if (!registration) {
        const originalType = heading.attributes.type || (placeholderPath ? INSTANCE_PLACEHOLDER_TYPE : 'Node');
        // An instance node has no type until loaded, so only an unsupported type warns.
        if (!hasInstanceAttribute) {
            warn(`[NodeRegistry] Unsupported node type: ${originalType} - using Node fallback renderer`);
        }
        const nodeRegistration = nodeRegistry.getRegistration('Node');
        if (!nodeRegistration) {
            warn('[NodeRegistry] Node registration not found - cannot create fallback node');
            return null;
        }
        const parsedProps = nodeRegistration.parser(heading, properties);
        const node = {
            name: parsedProps.name,
            type: originalType,
            parent: parsedProps.parent,
            children: [],
            properties: parsedProps,
            rawProperties: properties,
            // One TscnParserCore scan built `properties`, so its key order is the node's file
            // order (ADR-0035).
            rawPropertiesOrderReliable: true,
        };
        if (isPropertyOverrideHeading(heading))
            node.overridesExistingNode = true;
        if (heading.attributes.owner)
            node.owner = heading.attributes.owner;
        Object.assign(node, headingIndexField(heading));
        if (hasInstanceAttribute) {
            node.instance = instanceRef;
        }
        return node;
    }
    const parsedProps = registration.parser(heading, properties);
    const node = {
        name: parsedProps.name,
        type: registration.typeName,
        parent: parsedProps.parent,
        children: [],
        properties: parsedProps,
        rawProperties: properties,
        // One TscnParserCore scan built `properties`, so its key order is the node's file
        // order (ADR-0035).
        rawPropertiesOrderReliable: true,
    };
    if (heading.attributes.owner)
        node.owner = heading.attributes.owner;
    Object.assign(node, headingIndexField(heading));
    // `instance` can be a heading attribute or a body property.
    if (heading.attributes.instance) {
        node.instance = heading.attributes.instance;
    }
    else if (properties.instance) {
        node.instance = properties.instance;
    }
    return node;
}
//# sourceMappingURL=NodeRegistry.js.map